import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import Select from "../../../components/forms/Select/Select.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import Pagination from "../../../components/ui/Pagination/Pagination.jsx";
import BulkImportModal from "../../../components/bulk/BulkImportModal.jsx";
import ConfirmModal from "../../../components/ui/Modal/ConfirmModal.jsx";
import BulkDeleteBar from "../../../components/ui/BulkDeleteBar/BulkDeleteBar.jsx";
import TriStateCheckbox from "../../../components/ui/TriStateCheckbox/TriStateCheckbox.jsx";
import { RATES, WEIGHTS, visibleMonthOptions } from "../../../data/rates.js";
import { useApp } from "../../../hooks/useApp.js";
import { useRowSelection } from "../../../hooks/useRowSelection.js";
import { money, round2 } from "../../../utils/format.js";
import { truckTax } from "../../../utils/tax.js";
import { isValidVin, sanitizeVinInput } from "../../../utils/validators.js";
import { parseYesNo } from "../../../utils/bulkImport.js";
import { getWeightCategories, getFirstUsedMonths, getTaxYears } from "../../../services/lookupService.js";
import { getToken } from "../../../services/api.js";
import { saveFormType, updateReturnYearMonth } from "../../../services/returnService.js";

// A..V, ascending taxable-gross-weight order - same source TrucksStep uses
// for its weight-category dropdown.
const WEIGHT_CATEGORIES = Object.keys(RATES);

// Only TY2026 is offered for now - matches the current backend seed data
// (lookup_tax_years). Kept as the initial state so the field still renders
// immediately if the lookup fetch is slow or fails.
const DEFAULT_TAX_YEARS = [{ value: "2026" }];
const yearLabel = (year) => `${year}-${Number(year) + 1}`;

let nextRowId = 1;
const PAGE_SIZE = 10;

// Worksheet math (i2290 Line 3 worksheet): partial-period tax at the
// increase month for the new category, minus the same for the previous
// category. Reads the DB-backed partial-period rate (periodNormalAmt/
// periodLoggingAmt - already fetched into weightCategoriesLookup below for
// the category dropdowns) rather than re-deriving it client-side: the IRS
// i2290 Table I/II partial-period figures are NOT a straight annual x
// remainingMonths/12 proportion (e.g. category P logging for a month-8
// increase is 295.62 per the table, not the 295.625 that proportion works
// out to, which round2 would push to 295.63). truckTax()'s proportional
// approximation is kept only as a fallback for while the lookup is still
// loading.
const rowReady = (v, month) => !!(v.oldCat && v.newCat && month);
const periodRateFor = (weightCategoriesLookup, cat, logging, month) => {
  const w = weightCategoriesLookup.find((x) => x.code === cat);
  if (w) return Number(logging ? w.periodLoggingAmt : w.periodNormalAmt);
  return truckTax({ cat, logging, suspended: false }, month);
};
const newTaxFor = (weightCategoriesLookup, v, month) =>
  periodRateFor(weightCategoriesLookup, v.newCat, v.logging, month);
const prevTaxFor = (weightCategoriesLookup, v, month) =>
  periodRateFor(weightCategoriesLookup, v.oldCat, v.logging, month);
const additionalTaxFor = (weightCategoriesLookup, v, month) =>
  round2(newTaxFor(weightCategoriesLookup, v, month) - prevTaxFor(weightCategoriesLookup, v, month));

// TGWIncreaseWorksheet (per vehicle): i2290 Line 3 - "Complete line 3 only if
// the taxable gross weight of a vehicle increases during the period and the
// vehicle falls in a new category." One worksheet row per affected vehicle;
// AdditionalTaxAmt = NewTaxAmt - PreviousTaxAmt (TGWIncreaseWorksheet.xsd).
const WeightIncreaseStep = ({ onNext, onBack }) => {
  const { state, dispatch, showToast } = useApp();
  const [monthError, setMonthError] = useState(false);
  const [vehicleErrors, setVehicleErrors] = useState({});
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [yearSaving, setYearSaving] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);

  // Draft entry form - mirrors TrucksStep's add-then-list-in-a-table pattern:
  // fields live here until "+ Add vehicle" validates and pushes them into
  // state.amendVehicles, at which point the row becomes read-only in the table.
  const [draftVin, setDraftVin] = useState("");
  const [draftOldCat, setDraftOldCat] = useState("");
  const [draftNewCat, setDraftNewCat] = useState("");
  const [draftLogging, setDraftLogging] = useState(false);
  const [draftErrors, setDraftErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState(null);
  const localVinsRef = useRef(null);
  const isEditing = editingId != null;

  // Checkbox multi-select + bulk delete - separate from the single-row
  // pendingRemoveId state above, so the existing one-at-a-time Delete flow
  // is untouched. No backend call either way here (see removeVehicle's own
  // comment: amendVehicles is only ever persisted in bulk via saveFormType
  // on Continue), so bulk delete is just a local state update, same as
  // single-row delete already is.
  const selection = useRowSelection();
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const month = state.amendMonth;
  const vehicles = state.amendVehicles;
  const usingBackend = !!(getToken() && state.returnId);

  // Rejected-return-resubmission vehicle info (informational only, same pattern as
  // TrucksStep/VinCorrectionStep - adding more vehicles than originally filed is
  // allowed; an additional service fee applies before resubmitting, enforced
  // server-side at submit time). hasRejectionInfo is false for a plain draft
  // (state.originalVehicleCount is null until first submit) and for a rejected
  // return that predates this field (never captured a baseline either).
  const hasRejectionInfo = state.rejectedResubmission && state.originalVehicleCount != null;
  const extraPendingFee = state.extraVehiclesPendingFee ?? 0;
  const pageCount = Math.max(1, Math.ceil(vehicles.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageVehicles = vehicles.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  // "Select all" covers every loaded row, not just the current page - the
  // whole list is already loaded client-side (Requirement 15).
  const allRowIds = vehicles.map((v) => v.id);
  const selectedOnScreen = allRowIds.reduce((n, id) => n + (selection.selectedIds.has(id) ? 1 : 0), 0);
  const headerChecked = allRowIds.length > 0 && selectedOnScreen === allRowIds.length;
  const headerIndeterminate = selectedOnScreen > 0 && selectedOnScreen < allRowIds.length;

  const monthOptions = state.monthsLookup.length
    ? state.monthsLookup.map((m) => [m.value, m.label])
    : visibleMonthOptions().map((m) => [m[0], m[1]]);

  const taxYearOptions = state.taxYearsLookup.length ? state.taxYearsLookup : DEFAULT_TAX_YEARS;

  // Rate shown switches between the standard and logging PERIOD amount
  // (periodNormalAmt/periodLoggingAmt - the partial-period rate for "Month
  // the weight increased") as the draft's Logging checkbox is toggled - same
  // convention as TrucksStep, except keyed off the period rate rather than
  // the flat annual one, since NewTaxAmt/PreviousTaxAmt below are themselves
  // figured from that month, not always a full year.
  const catOptions = state.weightCategoriesLookup.length
    ? state.weightCategoriesLookup.map((w) => [
        w.code,
        `${w.code} · ${w.weightLabel} · ${money(draftLogging ? w.periodLoggingAmt : w.periodNormalAmt)}`,
      ])
    : WEIGHT_CATEGORIES.map((c) => [c, `${c} · ${WEIGHTS[c]} · ${money(RATES[c][draftLogging ? 1 : 0])}/yr`]);

  const validCatCodes = useMemo(
    () => new Set(state.weightCategoriesLookup.length ? state.weightCategoriesLookup.map((w) => w.code) : WEIGHT_CATEGORIES),
    [state.weightCategoriesLookup]
  );

  // "CODE - weight range" strings for the bulk-upload template's Excel
  // dropdown - see TrucksStep's catDropdownOptions for why. parse (below)
  // strips the " - ..." part back off before saving.
  const catDropdownOptions = useMemo(
    () =>
      (state.weightCategoriesLookup.length
        ? state.weightCategoriesLookup.map((w) => [w.code, w.weightLabel])
        : WEIGHT_CATEGORIES.map((c) => [c, WEIGHTS[c]])
      )
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([code, label]) => `${code} - ${label}`),
    [state.weightCategoriesLookup]
  );

  // Bulk-upload columns for the weight-increase worksheet table - keys
  // match the { vin, oldCat, newCat, logging } shape addVehicle (manual)
  // already builds, reused as-is by bulkAddRow below. This step has no
  // per-row backend endpoint (amendVehicles is only persisted in bulk via
  // saveFormType on Continue), so validation happens entirely client-side,
  // mirroring what addVehicle already checks.
  const bulkColumns = useMemo(
    () => [
      {
        key: "vin",
        header: "VIN",
        required: true,
        example: "1FUJGLDR9CLBP8834",
        description: "The vehicle whose taxable gross weight increased. Letters and numbers only.",
        parse: (v) => sanitizeVinInput(String(v ?? "")),
        validate: (v) => (isValidVin(v) ? "" : "Not a valid VIN."),
      },
      {
        key: "oldCat",
        header: "Previous category (A-W)",
        required: true,
        example: "C",
        description: "The vehicle's weight category before the increase. Pick from the dropdown - it shows each code's weight range.",
        dropdown: catDropdownOptions,
        parse: (v) => String(v ?? "").trim().split(" - ")[0].trim().toUpperCase(),
        validate: (v) => {
          if (!v) return "Previous category is required.";
          if (!validCatCodes.has(v)) return "Not a recognized weight category.";
          return "";
        },
      },
      {
        key: "newCat",
        header: "New category (A-W)",
        required: true,
        example: "D",
        description: "The vehicle's weight category after the increase. Must be higher than the previous category.",
        dropdown: catDropdownOptions,
        parse: (v) => String(v ?? "").trim().split(" - ")[0].trim().toUpperCase(),
        validate: (v, row) => {
          if (!v) return "New category is required.";
          if (!validCatCodes.has(v)) return "Not a recognized weight category.";
          if (row.oldCat && WEIGHT_CATEGORIES.indexOf(v) <= WEIGHT_CATEGORIES.indexOf(row.oldCat)) {
            return "Must be higher than the previous category.";
          }
          return "";
        },
      },
      {
        key: "logging",
        header: "Logging (Yes/No)",
        required: false,
        example: "No",
        description: "Yes if this truck is used exclusively for hauling harvested forest products (lower tax rate).",
        parse: parseYesNo,
      },
    ],
    [validCatCodes, catDropdownOptions]
  );

  // No backend per-row endpoint for this table (see bulkColumns comment
  // above) - called once per parsed row by BulkImportModal, purely local,
  // same validation addVehicle() does by hand (VIN format + not already on
  // this list, category ordering). localVinsRef tracks VINs added so far in
  // this batch (cleared whenever the modal is (re)opened), same pattern as
  // TrucksStep/AmendedReturnStep's bulk import.
  const bulkAddRow = async (payload) => {
    if (!localVinsRef.current) localVinsRef.current = new Set(vehicles.map((r) => r.vin));
    if (localVinsRef.current.has(payload.vin)) {
      throw new Error("That VIN is already on this list.");
    }
    localVinsRef.current.add(payload.vin);
    dispatch({
      type: "ADD_AMEND_VEHICLE",
      vehicle: { id: nextRowId++, vin: payload.vin, oldCat: payload.oldCat, newCat: payload.newCat, logging: payload.logging },
    });
    return payload;
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([getFirstUsedMonths(), getTaxYears()])
      .then(([months, taxYears]) => {
        if (cancelled) return;
        if (Array.isArray(months) && months.length) dispatch({ type: "SET_LOOKUPS", months });
        if (Array.isArray(taxYears) && taxYears.length) {
          dispatch({ type: "SET_LOOKUPS", taxYears: taxYears.map((y) => ({ value: y.code })) });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Weight categories carry a per-month partial-period rate - refetch
  // whenever "Month the weight increased" changes so the Previous/New
  // category dropdowns always reflect that month's rate, not just July's.
  useEffect(() => {
    let cancelled = false;
    getWeightCategories(month)
      .then((weightCategories) => {
        if (!cancelled && Array.isArray(weightCategories) && weightCategories.length) {
          dispatch({ type: "SET_LOOKUPS", weightCategories });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const handleYearChange = async (e) => {
    const year = e.target.value;
    if (usingBackend) {
      setYearSaving(true);
      try {
        const r = await updateReturnYearMonth(state.returnId, { taxYear: year, firstUsedMonth: state.month });
        // formTypeCd on the return isn't persisted as "amended" until this
        // step's own Continue (saveFormType, below) - this mid-step call
        // echoes back the return's still-stale formTypeCd (e.g. "original"),
        // which would otherwise overwrite state.returnType via SET_RETURN's
        // `action.returnType || state.returnType` fallback and reroute the
        // wizard out of this step. Same bug/fix as VinCorrectionStep/
        // AmendedReturnStep. This step only renders once returnType is
        // already "amended", so pinning is correct.
        dispatch({ type: "SET_RETURN", ...r, returnType: "amended" });
      } catch (err) {
        showToast(err.message || "Couldn't save the tax year. Please try again.");
      } finally {
        setYearSaving(false);
      }
      return;
    }
    dispatch({ type: "SET_TAX_YEAR", year });
  };

  const resetDraft = () => {
    setDraftVin("");
    setDraftOldCat("");
    setDraftNewCat("");
    setDraftLogging(false);
    setDraftErrors({});
    setEditingId(null);
  };

  // Loads an existing row back into the draft form for editing - same
  // pattern as TrucksStep's startEdit.
  const startEdit = (row) => {
    setDraftVin(row.vin);
    setDraftOldCat(row.oldCat);
    setDraftNewCat(row.newCat);
    setDraftLogging(!!row.logging);
    setDraftErrors({});
    setEditingId(row.id);
  };

  // Validate-then-add/update. Unlike the rest of this step's fields (which
  // only reach the backend in bulk via saveFormType on Continue), each
  // vehicle is now saved to the backend right away here, the moment "+ Add
  // vehicle"/"Update vehicle" is clicked - same immediate-save timing as
  // TrucksStep.submitTruck - rather than waiting for Continue. There's no
  // per-row amend-vehicle endpoint, so this reuses saveFormType with the
  // full (updated) vehicle list; Continue's own saveFormType call still runs
  // afterward as a safety net that also carries the month/other fields.
  const addVehicle = async () => {
    const v = draftVin.trim().toUpperCase();
    const errs = {};
    if (!isValidVin(v)) errs.vin = "Enter a valid VIN.";
    else if (vehicles.some((r) => r.vin === v && r.id !== editingId)) errs.vin = "That VIN is already on this list.";
    if (!draftOldCat) errs.oldCat = "Previous category is required.";
    if (!draftNewCat) errs.newCat = "New (higher) category is required.";
    // The weight increase must move the vehicle into a higher category
    // (i2290 Line 3: "the taxable gross weight...increases...and the
    // vehicle falls in a new category").
    if (draftOldCat && draftNewCat && WEIGHT_CATEGORIES.indexOf(draftNewCat) <= WEIGHT_CATEGORIES.indexOf(draftOldCat)) {
      errs.newCat = "The new category must be higher than the previous category.";
    }
    setDraftErrors(errs);
    if (Object.keys(errs).length) return;

    const patch = { vin: v, oldCat: draftOldCat, newCat: draftNewCat, logging: draftLogging };
    const newId = isEditing ? editingId : nextRowId++;
    const nextVehicles = isEditing
      ? vehicles.map((r) => (r.id === editingId ? { ...r, ...patch } : r))
      : [...vehicles, { id: newId, ...patch }];

    if (usingBackend) {
      if (!month) {
        setMonthError(true);
        return;
      }
      setVehicleSaving(true);
      try {
        const r = await saveFormType(state.returnId, {
          returnType: "amended",
          addressChange: state.addressChange,
          amendReason: "weight",
          amendMonth: month,
          amendVehicles: nextVehicles,
          taxPreparerId: state.returnTaxPreparerId,
        });
        dispatch({ type: "SET_RETURN", ...r, returnType: "amended" });
        resetDraft();
        setVehicleErrors({});
      } catch (err) {
        applyFieldErrors(err);
        showToast(err.message || "Couldn't save the vehicle. Please try again.");
      } finally {
        setVehicleSaving(false);
      }
      return;
    }

    if (isEditing) {
      dispatch({ type: "UPDATE_AMEND_VEHICLE", id: editingId, patch });
    } else {
      dispatch({ type: "ADD_AMEND_VEHICLE", vehicle: { id: newId, ...patch } });
    }
    resetDraft();
    setVehicleErrors({});
  };

  // No per-row backend endpoint for this table (amendVehicles is only
  // persisted in bulk via saveFormType on Continue - see bulkColumns comment
  // above), so unlike VinCorrectionStep's confirm-then-await-delete, this is
  // just local state - confirm, then remove immediately.
  const removeVehicle = (id) => setPendingRemoveId(id);

  const cancelRemoveVehicle = () => setPendingRemoveId(null);

  const confirmRemoveVehicle = () => {
    const id = pendingRemoveId;
    dispatch({ type: "REMOVE_AMEND_VEHICLE", id });
    if (editingId === id) resetDraft();
    setPendingRemoveId(null);
  };

  // Bulk sibling of removeVehicle/confirmRemoveVehicle above - same
  // local-only reasoning (no per-row backend endpoint), so it's a single
  // synchronous dispatch, not an async call. Still confirms first (via the
  // ConfirmModal below), matching every other table's bulk-delete UX even
  // though single-row delete here happens to also always confirm.
  const openBulkDelete = () => setBulkConfirmOpen(true);
  const cancelBulkDelete = () => setBulkConfirmOpen(false);
  const confirmBulkDelete = () => {
    const count = selection.count;
    const ids = selection.selectedIds;
    dispatch({ type: "BULK_REMOVE_AMEND_VEHICLES", ids });
    if (editingId != null && ids.has(editingId)) resetDraft();
    selection.clear();
    setBulkConfirmOpen(false);
    showToast(`${count} vehicle${count === 1 ? "" : "s"} deleted successfully.`);
  };

  // Maps backend field errors like "amendVehicles[2].newCategoryCd" to a
  // plain per-row message list - rows are read-only once added (no more
  // in-place edit fields to attach an inline error to), so backend
  // rejections surface as a small list above the table instead.
  const applyFieldErrors = (err) => {
    const fieldErrors = err?.body?.error?.errors;
    if (!Array.isArray(fieldErrors)) return;
    const next = {};
    fieldErrors.forEach((fe) => {
      const m = /^amendVehicles\[(\d+)\]\.(\w+)$/.exec(fe.field || "");
      if (m) {
        const row = vehicles[Number(m[1])];
        next[row?.id ?? m[1]] = `${row?.vin || `Row ${Number(m[1]) + 1}`}: ${fe.message}`;
      } else if (fe.field === "amendVehicles") {
        next.none = fe.message;
      } else if (fe.field === "amendMonthWeight") {
        setMonthError(true);
      }
    });
    setVehicleErrors(next);
  };

  const handleContinue = async () => {
    if (!month) {
      setMonthError(true);
      return;
    }
    if (!vehicles.length) {
      setVehicleErrors({ none: "Add at least one affected vehicle." });
      return;
    }
    setVehicleErrors({});

    if (usingBackend) {
      setSaving(true);
      try {
        const r = await saveFormType(state.returnId, {
          returnType: "amended",
          addressChange: state.addressChange,
          amendReason: "weight",
          amendMonth: month,
          amendVehicles: vehicles,
          taxPreparerId: state.returnTaxPreparerId,
        });
        dispatch({ type: "SET_RETURN", ...r });
      } catch (err) {
        setSaving(false);
        applyFieldErrors(err);
        showToast(err.message || "Couldn't save. Please try again.");
        return;
      }
      setSaving(false);
    }

    onNext();
  };

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
        Amended return · Weight increase
      </div>
      <h2 className="text-2xl m-[4px_0]">Taxable gross weight increased</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">
        Figure the additional tax for each vehicle whose taxable gross weight moved into a higher category during the
        period.
      </p>

      <FormField label="Tax year" hint={yearSaving ? "Saving…" : undefined} style={{ maxWidth: 300 }}>
        <Select value={state.year} onChange={handleYearChange} disabled={lookupsLoading || yearSaving}>
          {taxYearOptions.map((y) => (
            <option key={y.value} value={y.value}>
              {yearLabel(y.value)}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField
        label="Month the weight increased"
        required
        hint="Written next to the Amended Return box — one month per amended return. Trucks that increased in a different month go on a separate amended return."
        error="Pick the month the weight increased."
        showError={monthError}
        style={{ maxWidth: 300 }}
      >
        <Select
          value={month}
          onChange={(e) => {
            dispatch({ type: "SET_AMEND_MONTH", month: e.target.value });
            setMonthError(false);
          }}
          invalid={monthError}
          disabled={lookupsLoading}
        >
          <option value="">Select month…</option>
          {monthOptions.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </FormField>

      <h3 className="text-[15px] font-semibold mt-4 mb-2">Vehicles whose weight increased</h3>
      <p className="text-[var(--muted)] text-sm mb-2.5">
        Add each vehicle whose taxable gross weight went up into a higher category during the period.
      </p>

      {hasRejectionInfo && (
        <div
          className="rounded-[var(--r-sm)] border p-3 mb-4 text-[13px]"
          style={
            extraPendingFee > 0
              ? { borderColor: "var(--amber)", background: "var(--amber-soft)", color: "var(--amber)" }
              : { borderColor: "var(--line-2)", background: "var(--panel-2)", color: "var(--muted)" }
          }
        >
          <b className="font-semibold" style={{ color: extraPendingFee > 0 ? "var(--amber)" : "var(--ink)" }}>
            Vehicles: {vehicles.length} (originally filed: {state.originalVehicleCount})
          </b>
          <div className="mt-0.5">
            {extraPendingFee > 0
              ? `You've added ${extraPendingFee} vehicle${extraPendingFee === 1 ? "" : "s"} beyond what was originally filed — an additional service fee applies before this return can be resubmitted (you'll pay it on the Payment Details step).`
              : `This return was rejected by the IRS. You can add, remove, or correct vehicles freely — adding more than the ${state.originalVehicleCount} originally filed will require an additional service fee before resubmitting.`}
          </div>
        </div>
      )}

      <Panel tone="soft" className="!shadow-none p-[18px] mb-3">
        <div className="flex items-center justify-between mb-3.5">
          <h3 className="text-[19px]">
            {isEditing ? "Update this vehicle" : "Add a vehicle"}
          </h3>
          {!isEditing && (
            <Button variant="ghost" onClick={() => setBulkOpen(true)}>
              ⇪ Bulk upload
            </Button>
          )}
        </div>
        <FormField label="VIN" required error={draftErrors.vin} showError={!!draftErrors.vin}>
          <TextInput
            mono
            maxLength={19}
            placeholder="Vehicle whose weight increased"
            value={draftVin}
            onChange={(e) => setDraftVin(sanitizeVinInput(e.target.value))}
            invalid={!!draftErrors.vin}
            disabled={vehicleSaving}
          />
        </FormField>

        <div className="grid gap-3.5 sm:grid-cols-2">
          <FormField label="Previous category" required error={draftErrors.oldCat} showError={!!draftErrors.oldCat}>
            <Select
              value={draftOldCat}
              onChange={(e) => setDraftOldCat(e.target.value)}
              invalid={!!draftErrors.oldCat}
              disabled={vehicleSaving}
            >
              <option value="">Category…</option>
              {catOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="New (higher) category" required error={draftErrors.newCat} showError={!!draftErrors.newCat}>
            <Select
              value={draftNewCat}
              onChange={(e) => setDraftNewCat(e.target.value)}
              invalid={!!draftErrors.newCat}
              disabled={vehicleSaving}
            >
              <option value="">Category…</option>
              {catOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>
        </div>

        <Checkbox
          id="weightIncLog"
          title="Logging truck"
          subtitle="Applies the lower logging rate."
          checked={draftLogging}
          onChange={(e) => setDraftLogging(e.target.checked)}
          disabled={vehicleSaving}
        />

        <div className="flex gap-2">
          <Button variant="ghost" onClick={addVehicle} disabled={vehicleSaving}>
            {vehicleSaving ? "Saving…" : isEditing ? "Update vehicle" : "+ Add vehicle"}
          </Button>
          {isEditing && (
            <Button variant="ghost" onClick={resetDraft} disabled={vehicleSaving}>
              Cancel
            </Button>
          )}
        </div>
      </Panel>

      <BulkDeleteBar count={selection.count} onDelete={openBulkDelete} />
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left p-3 border-b border-[var(--line)] w-[36px]">
              <TriStateCheckbox
                checked={headerChecked}
                indeterminate={headerIndeterminate}
                onChange={() => selection.selectAll(headerChecked ? [] : allRowIds)}
                label="Select all vehicles"
              />
            </th>
            {["#", "VIN", "Previous cat", "New cat", "Type", "New tax", "Previous tax", "Additional tax", "Actions"].map(
              (h, hi, arr) => (
                <th
                  key={h}
                  className={[
                    "p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold",
                    hi === arr.length - 1 ? "text-right" : "text-left",
                  ].join(" ")}
                >
                  {h}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {vehicles.length === 0 ? (
            <tr>
              <td colSpan={10} className="text-[var(--muted)] p-[22px_12px]">
                No vehicles yet. Add your first one above.
              </td>
            </tr>
          ) : (
            pageVehicles.map((row, pageIndex) => (
              <tr key={row.id} className="hover:bg-[var(--accent-soft)] transition-colors">
                <td className="text-left p-3 border-b border-[var(--line)]">
                  <TriStateCheckbox
                    checked={selection.selectedIds.has(row.id)}
                    onChange={() => selection.toggle(row.id)}
                    label={`Select row ${safePage * PAGE_SIZE + pageIndex + 1}`}
                  />
                </td>
                <td className="text-left p-3 border-b border-[var(--line)]">{safePage * PAGE_SIZE + pageIndex + 1}</td>
                <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{row.vin}</td>
                <td className="text-left p-3 border-b border-[var(--line)]">{row.oldCat}</td>
                <td className="text-left p-3 border-b border-[var(--line)]">{row.newCat}</td>
                <td className="text-left p-3 border-b border-[var(--line)]">{row.logging ? "Logging" : "Standard"}</td>
                <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">
                  {rowReady(row, month) ? money(newTaxFor(state.weightCategoriesLookup, row, month)) : "—"}
                </td>
                <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">
                  {rowReady(row, month) ? money(prevTaxFor(state.weightCategoriesLookup, row, month)) : "—"}
                </td>
                <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">
                  {rowReady(row, month) ? money(additionalTaxFor(state.weightCategoriesLookup, row, month)) : "—"}
                </td>
                <td className="text-right p-3 border-b border-[var(--line)] whitespace-nowrap">
                  <button
                    type="button"
                    title="Edit"
                    onClick={() => startEdit(row)}
                    className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 hover:text-[var(--accent)]"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    title="Delete"
                    onClick={() => removeVehicle(row.id)}
                    className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 ml-1 hover:text-[var(--danger)]"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />
      {Object.values(vehicleErrors).map((msg, i) => (
        <div key={i} className="text-xs text-[var(--danger)] mb-1 mt-3">
          {msg}
        </div>
      ))}

      <div className="flex justify-between items-baseline mt-3.5 pt-3 border-t-2 border-[var(--ink)]">
        <span className="font-disp uppercase tracking-[.08em] text-[13px]">Additional tax (line 3)</span>
        <b className="font-mono text-2xl font-semibold">
          {money(
            round2(
              vehicles.reduce(
                (sum, v) => sum + (rowReady(v, month) ? additionalTaxFor(state.weightCategoriesLookup, v, month) : 0),
                0
              )
            )
          )}
        </b>
      </div>

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <Button onClick={handleContinue} disabled={saving}>
          {saving ? "Saving…" : "Continue →"}
        </Button>
      </div>

      <BulkImportModal
        open={bulkOpen}
        onClose={() => {
          setBulkOpen(false);
          localVinsRef.current = null;
        }}
        title="Bulk upload vehicles"
        columns={bulkColumns}
        filename="form2290-weight-increase-template.xlsx"
        sheetName="Vehicles"
        addRow={bulkAddRow}
        templateId="form2290-weight-increase-v1"
      />

      <ConfirmModal
        open={pendingRemoveId != null}
        title="Delete Confirmation"
        confirmLabel="Delete"
        onConfirm={confirmRemoveVehicle}
        onCancel={cancelRemoveVehicle}
      >
        Are you sure you want to delete this record? This action cannot be undone.
      </ConfirmModal>

      <ConfirmModal
        open={bulkConfirmOpen}
        title="Delete Selected Records?"
        confirmLabel="Delete"
        onConfirm={confirmBulkDelete}
        onCancel={cancelBulkDelete}
      >
        Are you sure you want to delete these {selection.count} record{selection.count === 1 ? "" : "s"}? This action
        cannot be undone.
      </ConfirmModal>
    </Panel>
  );
};

export default WeightIncreaseStep;
